// eslint-disable-next-line import/no-named-as-default
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import { isArray, isPlainObject, isString, mapValues } from 'lodash';

/**
 * Single entry point for rendering markup that did not come from this code base: stored template bodies,
 * SVG icons from settings, translation values. Two levels are offered:
 *
 * - `sanitizeHtml` / `sanitizeSvg` / `parseSanitizedHtml` run DOMPurify with an allowlist. Use them for
 *   snippets that are shown in the app itself (tooltips, hints, icons). Unknown tags and attributes are dropped.
 * - `stripExecutableHtml` only removes what can run script (script-like elements, `on*` handlers, `javascript:`
 *   URLs, `srcdoc`, meta refresh) and keeps everything else, including comments and unknown tags. Use it for
 *   email and page templates that are handed to an editor, where Outlook conditional comments, VML and
 *   namespaced attributes must survive. It is not an allowlist, so never use it to render markup in the app.
 */

type ParsedHtml = ReturnType<typeof parse>;

const HTML_SNIPPET_CONFIG = { USE_PROFILES: { html: true }, ADD_ATTR: ['target'] };
const SVG_CONFIG = { USE_PROFILES: { svg: true, svgFilters: true } };

/** Elements whose presence alone can run script or load another document into the page. */
const EXECUTABLE_TAGS_SELECTOR = ['script', 'iframe', 'frame', 'frameset', 'object', 'embed', 'applet', 'base'].join(
    ',',
);

/** Attributes that take a URL the browser may navigate to or execute. */
const URL_ATTRIBUTES = new Set([
    'href',
    'xlink:href',
    'src',
    'action',
    'formaction',
    'poster',
    'background',
    'data',
    'codebase',
]);

/** `javascript:` and `vbscript:` schemes and `data:` HTML documents, tested after control characters are removed. */
const EXECUTABLE_URL = /^(?:javascript|vbscript):|^data:\s*text\/html/i;

/** A tag, comment or closing tag. `a < b` is text for the HTML parser as well and is returned untouched. */
const LOOKS_LIKE_HTML = /<[a-z!/]/i;

/** Browsers ignore ASCII control characters and spaces inside a scheme (`java\tscript:`), so they are dropped first. */
const withoutControlCharacters = (value: string): string =>
    Array.from(value)
        .filter((character) => character.charCodeAt(0) > 0x20)
        .join('');

const isExecutableUrl = (value: string): boolean => EXECUTABLE_URL.test(withoutControlCharacters(value));

const stripExecutableNodes = (root: ParentNode): void => {
    root.querySelectorAll(EXECUTABLE_TAGS_SELECTOR).forEach((element) => element.remove());

    root.querySelectorAll('meta[http-equiv]').forEach((element) => {
        if (/refresh/i.test(element.getAttribute('http-equiv') ?? '')) element.remove();
    });

    root.querySelectorAll('*').forEach((element) => {
        Array.from(element.attributes).forEach((attribute) => {
            const name = attribute.name.toLowerCase();

            if (
                name.startsWith('on') ||
                name === 'srcdoc' ||
                (URL_ATTRIBUTES.has(name) && isExecutableUrl(attribute.value))
            ) {
                element.removeAttribute(attribute.name);
            }
        });

        // `querySelectorAll` does not descend into <template> content
        if (element instanceof HTMLTemplateElement) stripExecutableNodes(element.content);
    });
};

/** DOMPurify with the HTML profile. Links keep `target`, everything outside the allowlist is dropped. */
export const sanitizeHtml = (html: Nilable<string>): string => DOMPurify.sanitize(html ?? '', HTML_SNIPPET_CONFIG);

/** DOMPurify with the SVG profile, for inline icons. Scripts, handlers, `foreignObject` and external refs are dropped. */
export const sanitizeSvg = (svg: Nilable<string>): string => DOMPurify.sanitize(svg ?? '', SVG_CONFIG);

/** `html-react-parser` over `sanitizeHtml`. The only way markup from data or translations should become React nodes. */
export const parseSanitizedHtml = (html: Nilable<string>): ParsedHtml => parse(sanitizeHtml(html));

/**
 * Removes everything that can execute from a template body and keeps the rest as it is, so the result can be
 * loaded into an editor. The document is parsed with `DOMParser` (nothing runs while parsing) and serialized
 * back the same way `replaceLinksTarget` in `services/template` does: whole document, body, or fragment,
 * depending on the last closing tag. Strings that do not look like markup are returned untouched.
 */
export const stripExecutableHtml = (html: Nilable<string>): string => {
    if (!isString(html)) return '';
    if (!LOOKS_LIKE_HTML.test(html)) return html;

    const htmlDocument = new DOMParser().parseFromString(html, 'text/html');

    stripExecutableNodes(htmlDocument);

    const lastClosingTag = html.match(/<\/([a-z]+)>\s*$/i)?.[1]?.toLowerCase();

    if (lastClosingTag === 'html') {
        const doctype = htmlDocument.doctype ? `${new XMLSerializer().serializeToString(htmlDocument.doctype)}\n` : '';

        return `${doctype}${htmlDocument.documentElement.outerHTML}`;
    }

    if (lastClosingTag === 'body') {
        return htmlDocument.body.outerHTML;
    }

    // A fragment that starts with <style> or <link> has those moved into <head> by the parser; keep them.
    return `${htmlDocument.head.innerHTML}${htmlDocument.body.innerHTML}`;
};

/** Applies `stripExecutableHtml` to every string inside a JSON-like value (editor configs, block contents). */
export const stripExecutableHtmlDeep = <T>(value: T): T => {
    if (isString(value)) return stripExecutableHtml(value) as unknown as T;
    if (isArray(value)) return value.map(stripExecutableHtmlDeep) as unknown as T;
    if (isPlainObject(value)) return mapValues(value as Record<string, unknown>, stripExecutableHtmlDeep) as T;

    return value;
};
